import type {
  add as addMessage,
  set as setMessages,
  update as updateMessage,
  _updateById as updateById,
  updateWithBreak,
  clear as clearMessages,
  _remove as removeMessage,
  _removeMany as removeManyMessages,
  _readMessagesInChat
} from '@/store/messages/messages'
import type { setActiveChatId } from '@/store/chats/chats'
import type { setNews, _changeNewsViewed } from '@/store/news/news'
import type {
  IShortMessage,
  MessageEngineListenersMap,
  MessageEngineProps,
  MessageList,
  MessageListenersList,
  ShowedMessage
} from './MessageEngineTypes'
import type { IBreakMessage, IDateMessage, IMarkMessage, IMessage } from '@/store/messages/interfaces'
import { v4 as uuid } from 'uuid'
import { isShortMessage } from '@/engine/MessageEngine/predicates'
import { MessageEngineEvents } from '@/engine/MessageEngine/constants'
import { CustomChatNames } from '@/constants/CustomChats'
import type { GetPayload } from '../interfaces'
import { MessageStore } from './MessageStore'
import { newsToMessage } from './newsMapper'

class MessageEngine {
  private readonly getState: MessageEngineProps['getState']
  private readonly lastMessagesCache: MessageEngineProps['lastMessagesCache']
  private readonly listenersList: MessageListenersList = {}
  private readonly messageStore = new Map<string, MessageStore>()
  private messageList: MessageList = {}
  private lastMessageList: Record<string, IShortMessage | undefined> = {}
  private unreadCountList: Record<string, number> = {}
  private activeChatId: string | null = null
  private unreadMarkList: Record<string, IMarkMessage | undefined> = {}
  private staleUnreadMarks = new Set<string>()
  private breakList: Record<string, IBreakMessage[]> = {}
  private loadedChats = new Set<string>()

  constructor(props: MessageEngineProps) {
    this.getState = props.getState
    this.lastMessagesCache = props.lastMessagesCache
    this.bindListeners()
    this.lastMessagesCache.subscribe(this.onLastMessagesCacheChange)
  }

  // Memoized short projections of cached messages, so `getLastMessage` returns a
  // stable reference (required by useSyncExternalStore) until the message changes.
  private cachedShortMessages: Record<string, IShortMessage> = {}

  // Cached last messages are shown until real history is loaded. When the cache
  // resolves, wake up subscribers of chats that have no real last message yet.
  private onLastMessagesCacheChange = () => {
    const cached = this.lastMessagesCache.getAll()
    for (const jid in cached) {
      if (!this.lastMessageList[jid]) {
        this.notify(MessageEngineEvents.LastMessage, jid, jid)
      }
    }
  }

  private getCachedShortMessage(chatId: string): IShortMessage | undefined {
    const message = this.lastMessagesCache.getMessage(chatId)
    if (!message) {
      return undefined
    }
    const cached = this.cachedShortMessages[chatId]
    if (
      cached
      && cached.id === message.id
      && cached.timestamp === message.timestamp
      && cached.status === message.status
    ) {
      return cached
    }
    const projection: IShortMessage = {
      id: message.id,
      type: 'shortMessage',
      timestamp: message.timestamp,
      from: message.from,
      status: message.status
    }
    this.cachedShortMessages[chatId] = projection
    return projection
  }

  private bindListeners() {
    this.onSetMessages = this.onSetMessages.bind(this)
    this.onAddMessage = this.onAddMessage.bind(this)
    this.onUpdateMessage = this.onUpdateMessage.bind(this)
    this.onUpdateById = this.onUpdateById.bind(this)
    this.onRemoveMessage = this.onRemoveMessage.bind(this)
    this.onRemoveMessages = this.onRemoveMessages.bind(this)
    this.onClearMessages = this.onClearMessages.bind(this)
    this.onSetUser = this.onSetUser.bind(this)
    this.onSetActiveChatId = this.onSetActiveChatId.bind(this)
    this.onUpdateWithBreak = this.onUpdateWithBreak.bind(this)
    this.onSetNews = this.onSetNews.bind(this)
    this.onChangeNewsViewed = this.onChangeNewsViewed.bind(this)
    this.onReadAllNews = this.onReadAllNews.bind(this)
    this.onReadAllMessages = this.onReadAllMessages.bind(this)
    this.getMessages = this.getMessages.bind(this)
    this.getLastMessage = this.getLastMessage.bind(this)
    this.getUnreadCount = this.getUnreadCount.bind(this)
    this.getMessagesCount = this.getMessagesCount.bind(this)
    this.getGlobalLastIncomingTimestamp = this.getGlobalLastIncomingTimestamp.bind(this)
    this.getLastMessages = this.getLastMessages.bind(this)
  }

  subscribe<K extends keyof MessageEngineListenersMap>(key: K, params: string, cb: MessageEngineListenersMap[K]) {
    if (!params) {
      return () => {}
    }

    if (!this.listenersList[key]) {
      this.listenersList[key] = {}
    }

    const events: Record<string, Set<MessageEngineListenersMap[K]> | undefined> | undefined = this.listenersList[key]

    if (!events[params]) {
      events[params] = new Set<MessageEngineListenersMap[K]>()
    }
    const listeners = events[params]
    listeners.add(cb)
    return () => {
      listeners.delete(cb)
      if (listeners.size === 0) {
        delete this.listenersList[key]?.[params]
      }
    }
  }

  private notify<K extends keyof MessageEngineListenersMap>(key: K, params: string, ...values: Parameters<MessageEngineListenersMap[K]>) {
    this.listenersList[key]?.[params]?.forEach((listener) => {
      try {
        listener(...values)
      } catch (err) {
        console.error(err) // eslint-disable-line no-console
      }
    })
  }

  private getStartDay(timestamp: number) {
    const date = new Date(timestamp)
    date.setHours(0, 0, 0, 0)
    return date.getTime()
  }

  private formatMessages(chatId: string, messages: readonly IShortMessage[]): ShowedMessage[] {
    if (!messages.length) {
      return []
    }
    const breaks = this.breakList[chatId]
    const combined: (IShortMessage | IBreakMessage)[] = breaks?.length
      ? [...messages, ...breaks].sort((a, b) => a.timestamp - b.timestamp)
      : [...messages]
    const showedMessages: ShowedMessage[] = []
    let lastDate = 0
    const markMessage = this.unreadMarkList[chatId]
    let markInserted = !markMessage
    combined.forEach(item => {
      const startDate = this.getStartDay(Math.trunc(item.timestamp / 1000))
      if (startDate !== lastDate) {
        const dateMessage: IDateMessage = {
          id: `${startDate}`,
          type: 'date',
          date: new Date(startDate)
        }
        showedMessages.push(dateMessage)
        lastDate = startDate
      }
      if (!markInserted && markMessage!.timestamp <= item.timestamp) {
        showedMessages.push(markMessage!)
        markInserted = true
      }
      showedMessages.push(item)
    })
    return showedMessages
  }

  private calcUnreadMark(chatId: string, messages: readonly IShortMessage[]): IMarkMessage | undefined {
    if (!messages.length) {
      return undefined
    }
    const userJid = this.getState().user.user?.$jid
    let firstUnread: IShortMessage | undefined
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i]
      if (message?.status !== 'displayed' && message?.from !== userJid) {
        firstUnread = message
      } else {
        break
      }
    }
    if (!firstUnread) {
      return undefined
    }
    return {
      id: `unread-mark:${chatId}`,
      type: 'mark',
      subType: 'unread-message',
      text: 'unread_messages',
      timestamp: firstUnread.timestamp
    }
  }

  private calcLastMessage(chatId: string) {
    const messages = this.messageList[chatId]
    if (!messages?.length) {
      return
    }
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i]
      if (isShortMessage(message)) {
        return message
      }
    }
  }

  private checkAndUpdateLastMessage(chatId: string, force = false) {
    const newLastMessage = this.calcLastMessage(chatId)
    if (!force && this.lastMessageList[chatId]?.id === newLastMessage?.id) {
      return
    }
    this.lastMessageList[chatId] = newLastMessage
    this.notify(MessageEngineEvents.LastMessage, chatId, chatId)
  }

  private calcUnreadCount(chatId: string): number {
    const messages = this.messageList[chatId]
    if (!messages?.length) {
      return 0
    }
    const userJid = this.getState().user.user?.$jid
    let count = 0
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i]
      if (!isShortMessage(message)) {
        continue
      }
      if (message.from === userJid) {
        break
      }
      if (message.status === 'displayed') {
        break
      }
      count++
    }
    return count
  }

  private checkAndUpdateUnreadMark(chatId: string, newMark: IMarkMessage | undefined) {
    const oldMark = this.unreadMarkList[chatId]
    if (oldMark?.timestamp === newMark?.timestamp) {
      return
    }
    if (newMark) {
      this.unreadMarkList[chatId] = newMark
    } else {
      delete this.unreadMarkList[chatId]
    }
    this.notify(MessageEngineEvents.UnreadMark, chatId, chatId)
  }

  private checkAndUpdateUnreadCount(chatId: string) {
    const newCount = this.calcUnreadCount(chatId)
    const oldCount = this.unreadCountList[chatId] ?? 0
    if (oldCount === newCount) {
      return
    }
    if (newCount === 0) {
      delete this.unreadCountList[chatId]
    } else {
      this.unreadCountList[chatId] = newCount
    }
    this.notify(MessageEngineEvents.UnreadCount, chatId, chatId)
  }

  private rebuildMessage(jid: string) {
    const store = this.messageStore.get(jid)
    if (!store) {
      return
    }
    const sorted = store.getSorted()
    const wasLoaded = this.loadedChats.has(jid)
    if (this.activeChatId !== jid || !wasLoaded) {
      this.checkAndUpdateUnreadMark(jid, this.calcUnreadMark(jid, sorted))
      this.staleUnreadMarks.delete(jid)
    }
    if (!wasLoaded && this.getState().messages.messages[jid]?.isLoaded) {
      this.loadedChats.add(jid)
    }
    this.messageList[jid] = this.formatMessages(jid, sorted)
    this.notify(MessageEngineEvents.Messages, jid, jid)
    this.checkAndUpdateLastMessage(jid)
    this.checkAndUpdateUnreadCount(jid)
  }

  private rebuildMessages(jids: Set<string> | string[]) {
    jids.forEach((jid) => {
      this.rebuildMessage(jid)
    })
  }

  private ensureStore(jid: string): MessageStore {
    let store = this.messageStore.get(jid)
    if (!store) {
      store = new MessageStore()
      this.messageStore.set(jid, store)
    }
    return store
  }

  onSetMessages(props: GetPayload<typeof setMessages>) {
    const { messages } = props
    const previousUnreadChats = Object.keys(this.unreadCountList)
    const previousUnreadMarkChats = Object.keys(this.unreadMarkList)
    this.messageList = {}
    this.lastMessageList = {}
    this.unreadCountList = {}
    this.staleUnreadMarks.clear()
    this.breakList = {}
    this.messageStore.clear()
    this.loadedChats.clear()
    const jids = new Set<string>()
    Object.entries(messages).forEach(([jid, messageItem]) => {
      this.ensureStore(jid).replaceAll(messageItem.messages)
      jids.add(jid)
    })
    const newsRecord = this.getState().news?.news
    if (newsRecord && Object.keys(newsRecord).length) {
      const mapped: Record<string, IMessage> = {}
      Object.values(newsRecord).forEach(n => { mapped[n.id] = newsToMessage(n) })
      this.ensureStore(CustomChatNames.ANNOUNCEMENTS).replaceAll(mapped)
      jids.add(CustomChatNames.ANNOUNCEMENTS)
    }
    previousUnreadMarkChats.forEach(chatId => {
      if (!jids.has(chatId)) {
        this.checkAndUpdateUnreadMark(chatId, undefined)
      }
    })
    if (this.activeChatId) {
      const store = this.messageStore.get(this.activeChatId)
      const newMark = store ? this.calcUnreadMark(this.activeChatId, store.getSorted()) : undefined
      this.checkAndUpdateUnreadMark(this.activeChatId, newMark)
    }
    this.rebuildMessages(jids)
    previousUnreadChats.forEach(chatId => {
      if (this.unreadCountList[chatId] == null) {
        this.notify(MessageEngineEvents.UnreadCount, chatId, chatId)
      }
    })
  }

  onAddMessage(props: GetPayload<typeof addMessage>) {
    const { jid, message } = props
    if (this.ensureStore(jid).add(message)) {
      this.rebuildMessage(jid)
    }
  }

  onUpdateMessage(props: GetPayload<typeof updateMessage>) {
    const propArr = Array.isArray(props) ? props : [props]
    const chatsForUpdate = new Set<string>()
    propArr.forEach(({ jid, messages }) => {
      const store = this.ensureStore(jid)
      messages.forEach((message) => {
        if (store.add(message)) {
          chatsForUpdate.add(jid)
        }
      })
    })
    this.rebuildMessages(chatsForUpdate)
  }

  onUpdateById(props: GetPayload<typeof updateById>) {
    const { chatJid, id, message } = props
    const store = this.messageStore.get(chatJid)
    if (!store) {
      return
    }
    if (message.id != null && message.id !== id) {
      const merged = this.getState().messages.messages[chatJid]?.messages[message.id]
      if (!merged) {
        return
      }
      store.remove(id)
      store.add(merged)
      this.rebuildMessage(chatJid)
      return
    }
    const tsChanged = message.timestamp != null && store.updateTimestamp(id, message.timestamp)
    const statusChanged = 'status' in message && store.updateStatus(id, message.status)
    if (tsChanged) {
      this.rebuildMessage(chatJid)
    } else if (statusChanged) {
      this.checkAndUpdateUnreadCount(chatJid)
    }
  }

  onRemoveMessage(props: GetPayload<typeof removeMessage>) {
    const { jid, messageId } = props
    const store = this.messageStore.get(jid)
    if (store?.remove(messageId)) {
      this.rebuildMessage(jid)
    }
  }

  onRemoveMessages(props: GetPayload<typeof removeManyMessages>) {
    const { jid, messageIds } = props
    const store = this.messageStore.get(jid)
    if (!store) {
      return
    }
    let removed = false
    messageIds.forEach(messageId => {
      if (store.remove(messageId)) {
        removed = true
      }
    })
    if (removed) {
      this.rebuildMessage(jid)
    }
  }

  onClearMessages(chatJid: GetPayload<typeof clearMessages>) {
    const hadUnread = this.unreadCountList[chatJid] != null
    const hadMark = this.unreadMarkList[chatJid] != null
    this.messageStore.delete(chatJid)
    delete this.messageList[chatJid]
    delete this.lastMessageList[chatJid]
    delete this.unreadCountList[chatJid]
    delete this.unreadMarkList[chatJid]
    this.staleUnreadMarks.delete(chatJid)
    delete this.breakList[chatJid]
    this.loadedChats.delete(chatJid)
    this.notify(MessageEngineEvents.Messages, chatJid, chatJid)
    this.notify(MessageEngineEvents.LastMessage, chatJid, chatJid)
    if (hadUnread) {
      this.notify(MessageEngineEvents.UnreadCount, chatJid, chatJid)
    }
    if (hadMark) {
      this.notify(MessageEngineEvents.UnreadMark, chatJid, chatJid)
    }
  }

  onSetActiveChatId(chatId: GetPayload<typeof setActiveChatId>) {
    const prevActive = this.activeChatId
    if (prevActive === chatId) return
    this.activeChatId = chatId

    if (prevActive) {
      this.staleUnreadMarks.add(prevActive)
    }

    if (chatId && this.staleUnreadMarks.has(chatId)) {
      const store = this.messageStore.get(chatId)
      if (store) {
        this.checkAndUpdateUnreadMark(chatId, this.calcUnreadMark(chatId, store.getSorted()))
        this.staleUnreadMarks.delete(chatId)
        this.rebuildMessage(chatId)
      }
    }
  }

  onUpdateWithBreak(payload: GetPayload<typeof updateWithBreak>) {
    const { chatJid, messages, breakId, insert = 'after' } = payload
    const messagesArray = Array.isArray(messages) ? messages : [messages]

    if (breakId) {
      this.removeBreak(chatJid, breakId)
    }

    if (!messagesArray.length) {
      this.rebuildMessage(chatJid)
      return
    }

    const store = this.ensureStore(chatJid)
    const sorted = [...messagesArray].sort((a, b) => a.timestamp - b.timestamp)
    const edgeMsg = insert === 'after' ? sorted[sorted.length - 1] : sorted[0]
    const edgeWasPresent = !!edgeMsg && store.has(edgeMsg.id)

    sorted.forEach(msg => store.add(msg))

    if (edgeMsg && !edgeWasPresent) {
      if (insert === 'before') {
        this.addBreak(chatJid, {
          id: uuid(),
          type: 'break',
          timestamp: edgeMsg.timestamp
        })
      } else {
        const sortedStore = store.getSorted()
        const idx = sortedStore.findIndex(m => m.id === edgeMsg.id)
        const next = idx >= 0 ? sortedStore[idx + 1] : undefined
        if (next) {
          this.addBreak(chatJid, {
            id: uuid(),
            type: 'break',
            timestamp: next.timestamp
          })
        }
      }
    }

    this.rebuildMessage(chatJid)
  }

  private addBreak(chatId: string, breakMsg: IBreakMessage) {
    if (!this.breakList[chatId]) this.breakList[chatId] = []
    this.breakList[chatId].push(breakMsg)
  }

  private removeBreak(chatId: string, breakId: string) {
    const list = this.breakList[chatId]
    if (!list) return
    const idx = list.findIndex(b => b.id === breakId)
    if (idx >= 0) list.splice(idx, 1)
    if (!list.length) delete this.breakList[chatId]
  }

  onSetNews(news: GetPayload<typeof setNews>) {
    const jid = CustomChatNames.ANNOUNCEMENTS
    const record: Record<string, IMessage> = {}
    news.forEach(n => { record[n.id] = newsToMessage(n) })
    this.ensureStore(jid).replaceAll(record)
    this.rebuildMessage(jid)
  }

  onChangeNewsViewed(payload: GetPayload<typeof _changeNewsViewed>) {
    const jid = CustomChatNames.ANNOUNCEMENTS
    const store = this.messageStore.get(jid)
    if (!store) return
    const { id, viewed = true } = payload
    const statusChanged = store.updateStatus(id, viewed ? 'displayed' : 'received')
    if (statusChanged) {
      this.checkAndUpdateUnreadCount(jid)
    }
  }

  onReadAllNews() {
    const jid = CustomChatNames.ANNOUNCEMENTS
    const store = this.messageStore.get(jid)
    if (!store) return
    const newsRecord = this.getState().news?.news
    if (!newsRecord) return
    let changed = false
    Object.values(newsRecord).forEach(n => {
      if (store.updateStatus(n.id, 'displayed')) changed = true
    })
    if (changed) {
      this.checkAndUpdateUnreadCount(jid)
    }
  }

  onReadAllMessages(payload: GetPayload<typeof _readMessagesInChat>) {
    const { chatJid, userJid } = payload
    const oldCount = this.unreadCountList[chatJid] || 0
    if (oldCount === 0) {
      return
    }
    this.messageList[chatJid]?.forEach(message => {
      if (isShortMessage(message) && message.from !== userJid && message.status !== 'displayed') {
        message.status = 'displayed'
      }
    })
    this.checkAndUpdateUnreadCount(chatJid)
  }

  onSetUser() {
    const chatIds = new Set<string>([
      ...Object.keys(this.messageList),
      ...Object.keys(this.unreadCountList)
    ])
    chatIds.forEach(chatId => {
      this.checkAndUpdateUnreadCount(chatId)
    })
  }

  getMessages(chatId: string): readonly ShowedMessage[] | undefined {
    return this.messageList[chatId]
  }

  getLastMessage(chatId: string): Readonly<IShortMessage> | undefined {
    return this.lastMessageList[chatId] ?? this.getCachedShortMessage(chatId)
  }

  getLastMessageFull(chatId: string): IMessage | undefined {
    return this.lastMessagesCache.getMessage(chatId)
  }

  getUnreadCount(chatId: string): number {
    return this.unreadCountList[chatId] ?? 0
  }

  getMessagesCount(chatId: string): number {
    return this.messageStore.get(chatId)?.size ?? 0
  }

  getLastMessages(jids?: readonly string[]): Readonly<Record<string, IMessage>> {
    const allMessages = this.getState().messages.messages
    const result: Record<string, IMessage> = {}
    const targetJids = jids ?? Object.keys(this.lastMessageList)
    targetJids.forEach(jid => {
      const shortMessage = this.lastMessageList[jid]
      if (!shortMessage) {
        return
      }
      const fullMessage = allMessages[jid]?.messages[shortMessage.id]
      if (fullMessage) {
        result[jid] = fullMessage
      }
    })
    return result
  }

  getGlobalLastIncomingTimestamp(): number {
    const userJid = this.getState().user.user?.$jid
    let max = 0
    this.messageStore.forEach(store => {
      const sorted = store.getSorted()
      for (let i = sorted.length - 1; i >= 0; i--) {
        const msg = sorted[i]
        if (!msg || msg.from === userJid) {
          continue
        }
        if (msg.timestamp > max) {
          max = msg.timestamp
        }
        break
      }
    })
    return max
  }

  getUnreadMark(jid: string): Readonly<IMarkMessage> | undefined {
    return this.unreadMarkList[jid]
  }
}

export { MessageEngine }
